import React, { useEffect, useRef } from 'react';
import { useProtectedImage } from '../../hooks/useProtectedImage';
import LoadingSpinner from '../globals/LoadingSpinner';

/**
 * Composant pour afficher les images protégées
 * 
 * Protections:
 * - Image servie en base64 (pas de fichier téléchargeable)
 * - Clic-droit désactivé
 * - Drag-and-drop désactivé
 * - Watermark appliqué côté serveur
 * - Impossible de télécharger via l'URL directe
 */
export const ProtectedImage = ({
  imagePath,
  alt = 'Image protégée',
  className = '',
  objectFit = 'cover',
  showWatermark = true,
  renderWatermark = null
}) => {
  const { image, loading, error } = useProtectedImage(imagePath);
  const imgRef = useRef(null);

  // Ajouter des protections supplémentaires
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Empêcher la copie de l'image
  const handleCopy = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    const imageElement = imgRef.current;
    if (!imageElement) return;

    imageElement.addEventListener('contextmenu', handleContextMenu);
    imageElement.addEventListener('dragstart', handleDragStart);
    imageElement.addEventListener('copy', handleCopy);
    
    return () => {
      imageElement?.removeEventListener('contextmenu', handleContextMenu);
      imageElement?.removeEventListener('dragstart', handleDragStart);
      imageElement?.removeEventListener('copy', handleCopy);
    };
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-red-100 text-red-600 ${className}`}>
        <div className="text-center">
          <p className="font-semibold">Impossible de charger l'image</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const defaultWatermark = () => {
    // Calculer dynamiquement le nombre de labels pour couvrir toute l'image
    const cols = 8;
    const rows = 12;
    const labels = Array.from({ length: cols * rows });
    
    return (
      <div className="absolute inset-0 overflow-hidden">
        {labels.map((_, i) => {
          const row = Math.floor(i / cols);
          const col = i % cols;
          // Alterner entre deux opacités pour une meilleure lisibilité
          const opacity = (row + col) % 2 === 0 ? 0.5 : 0.35;
          
          return (
            <div
              key={i}
              className="absolute text-base sm:text-lg md:text-xl lg:text-2xl font-black uppercase select-none whitespace-nowrap"
              style={{
                transform: `rotate(-35deg) translate(${col * 180 - 300}px, ${row * 80 - 200}px)`,
                color: `rgba(255, 255, 255, ${opacity})`,
                textShadow: '0 0 20px rgba(0, 0, 0, 1), 0 0 10px rgba(0, 0, 0, 0.8), 2px 2px 4px rgba(0, 0, 0, 0.9)',
                letterSpacing: '0.2em',
                pointerEvents: 'none',
                WebkitTextStroke: '0.5px rgba(0, 0, 0, 0.5)',
              }}
            >
              KRAYNE AGENCY
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <img
        ref={imgRef}
        src={image}
        alt={alt}
        className="w-full h-full select-none pointer-events-none"
        style={{
          objectFit: objectFit,
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        onCopy={handleCopy}
        draggable={false}
      />
      {showWatermark && (
        <div className="absolute inset-0 pointer-events-none">
          {renderWatermark ? renderWatermark() : defaultWatermark()}
        </div>
      )}
      {/* Protection anti-inspection */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          background: 'transparent',
          zIndex: 1
        }}
      />
    </div>
  );
};

export default ProtectedImage;
